import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { useQuery } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, ChevronDown, Clock, LogOut, Minus } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Route, Routes, useLocation, Navigate } from "react-router-dom";

import { getHsc, getMonitoreoRea, getTipoCambio } from "@/lib/api";
import { VacacionesWidget } from "@/components/VacacionesWidget";
import { currentEmail, DEV_BYPASS_AUTH } from "@/lib/auth";
import { LoginPage } from "@/pages/LoginPage";
import { ContratosDashboardPage } from "@/pages/ContratosDashboardPage";
import { ContratosGanttPage } from "@/pages/ContratosGanttPage";
import { BloombergPage } from "@/pages/BloombergPage";
import { DemandaPage } from "@/pages/DemandaPage";
import { DemandaHorariaPage } from "@/pages/DemandaHorariaPage";
import { DemandaRetirosPage } from "@/pages/DemandaRetirosPage";
import { MapaCalorPndPage } from "@/pages/MapaCalorPndPage";
import { MapaPreciosPage } from "@/pages/MapaPreciosPage";
import { ComprasMdaPage } from "@/pages/ComprasMdaPage";
import { ComprasMdaSeriePage } from "@/pages/ComprasMdaSeriePage";
import { OfertasCapacidadPage } from "@/pages/OfertasCapacidadPage";
import { MargenCapacidadPage } from "@/pages/MargenCapacidadPage";
import { DispersionHcPage } from "@/pages/DispersionHcPage";
import { PeriodoHcPage } from "@/pages/PeriodoHcPage";
import { PmlPndPage } from "@/pages/PmlPndPage";
import { RegistroProveedoresPage } from "@/pages/RegistroProveedoresPage";
import { TarifasCfePage } from "@/pages/TarifasCfePage";
import { TipoCambioPage } from "@/pages/TipoCambioPage";
import { ZcaPage } from "@/pages/ZcaPage";

export function App() {
  const isAuthenticated = useIsAuthenticated();
  const { instance } = useMsal();

  if (!DEV_BYPASS_AUTH && !isAuthenticated) {
    return <LoginPage />;
  }

  const email = currentEmail();

  const handleLogout = () => {
    if (DEV_BYPASS_AUTH) return;
    instance.logoutPopup({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  return (
    <div className="min-h-full flex flex-col">
      <header className="border-b bg-white px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-6">
          <Link to="/" className="flex items-center gap-3 hover:opacity-80">
            <img src="/logo-mark.png" alt="MPP Energía" className="w-8 h-8" />
            <div className="font-semibold text-lg">Inteligencia de Mercado</div>
          </Link>
          <nav className="flex items-center gap-1 text-sm">
            {/* Estados de cuenta CENACE vive en el ETRM (/reportes/edoctas) desde el 22-sep-2026. */}
            {/* Datos agrupa los dos submenús de consulta: Precios y Reportes (22-sep-2026). */}
            <NavDropdown
              label="Datos"
              groups={[
                {
                  label: "Precios",
                  items: [
                    { to: "/pml-pnd", label: "PML / PND" },
                    { to: "/zca", label: "Zonas agrupadas" },
                    { to: "/mapa-calor", label: "Mapa de calor" },
                    { to: "/mapa", label: "Mapa de Precios" },
                    { to: "/bloomberg", label: "Bloomberg" },
                    { to: "/tipo-cambio", label: "Tipo de cambio" },
                    { to: "/tarifas-cfe", label: "Tarifas CFE" },
                  ],
                },
                {
                  label: "Reportes",
                  items: [
                    { to: "/demanda", label: "Demanda histórica" },
                    { to: "/demanda-horaria", label: "Demanda Real" },
                    { to: "/demanda-retiros", label: "Retiros por centro de control" },
                    { to: "/compras-mda", label: "Compras MDA" },
                    { to: "/compras-mda/serie", label: "Compras MDA — línea de tiempo" },
                    { to: "/ofertas-capacidad", label: "Ofertas de capacidad (MTR)" },
                    { to: "/margen-capacidad", label: "Margen capacidad − demanda" },
                    { to: "/dispersion-hc", label: "Dispersión de Horas Críticas" },
                    { to: "/contratos", label: "Contratos — Dashboard", end: true },
                    { to: "/contratos/gantt", label: "Contratos — Gantt" },
                  ],
                },
              ]}
            />
            {/* Administración: captura de parámetros y altas (22-sep-2026). */}
            <NavDropdown
              label="Administración"
              items={[
                { to: "/periodo-hc", label: "Periodo de horas críticas (CENACE)" },
                { to: "/registro-proveedores", label: "Proveedores" },
              ]}
            />
          </nav>
        </div>
        <div className="flex items-center gap-4">
          <ReaSemaforo />
          <TipoCambioWidget />
          <HscWidget />
          <VacacionesWidget />
          <div className="text-sm text-slate-600">{email}</div>
          <button
            type="button"
            onClick={handleLogout}
            className="text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 text-sm"
            title="Cerrar sesión"
          >
            <LogOut className="w-4 h-4" />
          </button>
        </div>
      </header>
      <main className="flex-1 px-6 py-6">
        <Routes>
          <Route path="/" element={<Navigate to="/pml-pnd" replace />} />
          <Route path="/pml-pnd" element={<PmlPndPage />} />
          <Route path="/zca" element={<ZcaPage />} />
          <Route path="/mapa-calor" element={<MapaCalorPndPage />} />
          <Route path="/mapa" element={<MapaPreciosPage />} />
          <Route path="/compras-mda" element={<ComprasMdaPage />} />
          <Route path="/compras-mda/serie" element={<ComprasMdaSeriePage />} />
          <Route path="/ofertas-capacidad" element={<OfertasCapacidadPage />} />
          <Route path="/margen-capacidad" element={<MargenCapacidadPage />} />
          <Route path="/dispersion-hc" element={<DispersionHcPage />} />
          <Route path="/periodo-hc" element={<PeriodoHcPage />} />
          <Route path="/bloomberg" element={<BloombergPage />} />
          <Route path="/demanda" element={<DemandaPage />} />
          <Route path="/demanda-horaria" element={<DemandaHorariaPage />} />
          <Route path="/demanda-retiros" element={<DemandaRetirosPage />} />
          <Route path="/tipo-cambio" element={<TipoCambioPage />} />
          <Route path="/contratos" element={<ContratosDashboardPage />} />
          <Route path="/contratos/gantt" element={<ContratosGanttPage />} />
          <Route path="/tarifas-cfe" element={<TarifasCfePage />} />
          <Route path="/registro-proveedores" element={<RegistroProveedoresPage />} />
        </Routes>
      </main>
    </div>
  );
}

function HscWidget() {
  const q = useQuery({
    queryKey: ["hsc"],
    queryFn: getHsc,
    refetchInterval: 60 * 60_000,
    staleTime: 30 * 60_000,
  });

  if (q.isLoading || q.isError) return null;
  const data = q.data;
  if (!data || data.valor == null) return null;

  const v = Number(data.valor);
  const vPrev = data.valor_prev != null ? Number(data.valor_prev) : null;
  const diff =
    vPrev != null && Number.isFinite(vPrev) ? v - vPrev : null;
  const up = diff != null && diff > 0;
  const down = diff != null && diff < 0;

  const valText = Number.isFinite(v) ? v.toFixed(3) : "—";
  const diffText =
    diff != null && Number.isFinite(diff)
      ? `${diff > 0 ? "+" : ""}${diff.toFixed(3)}`
      : null;

  const fechaText = data.fecha
    ? new Date(data.fecha + "T00:00:00").toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      })
    : null;

  const trendCls = up
    ? "text-emerald-600"
    : down
      ? "text-red-600"
      : "text-slate-500";
  const TrendIcon = up ? ArrowUp : down ? ArrowDown : Minus;

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-white"
      title={`HSC — Bloomberg PGFCHOSC Index${diffText ? ` (vs día anterior ${diffText})` : ""}`}
    >
      <span className="text-[10px] font-semibold text-slate-400 tracking-wide">
        HSC
      </span>
      <div className="flex flex-col leading-tight">
        <span className="text-xs font-medium text-slate-700 tabular-nums inline-flex items-center gap-1">
          {valText}
          <TrendIcon className={`w-3 h-3 ${trendCls}`} />
          {diffText && (
            <span className={`text-[10px] ${trendCls} tabular-nums`}>
              {diffText}
            </span>
          )}
        </span>
        {fechaText && (
          <span className="text-[10px] text-slate-400 tabular-nums">
            {fechaText}
          </span>
        )}
      </div>
    </div>
  );
}

function TipoCambioWidget() {
  const q = useQuery({
    queryKey: ["tipo-cambio"],
    queryFn: getTipoCambio,
    refetchInterval: 60 * 60_000,
    staleTime: 30 * 60_000,
  });

  if (q.isLoading || q.isError) return null;
  const data = q.data;
  if (!data || data.valor == null) return null;

  const val = Number(data.valor);
  const valText = Number.isFinite(val)
    ? val.toLocaleString("es-MX", {
        style: "currency",
        currency: "MXN",
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      })
    : "—";

  const fechaText = data.fecha
    ? new Date(data.fecha + "T00:00:00").toLocaleDateString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
      })
    : null;

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-white"
      title="Tipo de cambio FIX USD/MXN (Banxico SF60653)"
    >
      <span
        className="inline-block text-[10px] font-semibold text-slate-400 tracking-wide"
        aria-hidden="true"
      >
        USD
      </span>
      <div className="flex flex-col leading-tight">
        <span className="text-xs font-medium text-slate-700 tabular-nums">
          {valText}
        </span>
        {fechaText && (
          <span className="text-[10px] text-slate-400 tabular-nums">
            {fechaText}
          </span>
        )}
      </div>
    </div>
  );
}

function ReaSemaforo() {
  const q = useQuery({
    queryKey: ["monitoreo-rea"],
    queryFn: getMonitoreoRea,
    refetchInterval: 60 * 60_000,
    staleTime: 30 * 60_000,
  });

  if (q.isLoading || q.isError) return null;
  const data = q.data;
  if (!data || data.rea_vs_mgp_pct == null) return null;

  const pct = Number(data.rea_vs_mgp_pct);
  const pctText = Number.isFinite(pct) ? `${pct.toFixed(1)}%` : "—";
  const color = data.bullet_color || "#94a3b8";

  const creado = data.creado_en ? new Date(data.creado_en) : null;
  const updatedText = creado
    ? creado.toLocaleString("es-MX", {
        day: "2-digit",
        month: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  // Datos sin actualizar por más de 12 horas: mostrar reloj en vez del semáforo.
  const HORAS_SIN_ACTUALIZAR = 12;
  const stale = creado
    ? Date.now() - creado.getTime() > HORAS_SIN_ACTUALIZAR * 60 * 60_000
    : false;

  return (
    <div
      className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-white"
      title={
        stale
          ? `REA vs MGP — sin actualizar${updatedText ? ` desde ${updatedText}` : ""}`
          : updatedText
            ? `REA vs MGP — actualizado ${updatedText}`
            : "REA vs MGP"
      }
    >
      {stale ? (
        <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" aria-hidden="true" />
      ) : (
        <span
          className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
          style={{
            backgroundColor: color,
            boxShadow: `0 0 0 3px ${color}22`,
          }}
          aria-hidden="true"
        />
      )}
      <div className="flex flex-col leading-tight">
        <span className="text-xs font-medium text-slate-700 tabular-nums">
          REA {pctText}
        </span>
        {updatedText && (
          <span className="text-[10px] text-slate-400 tabular-nums">
            {updatedText}
          </span>
        )}
      </div>
    </div>
  );
}

type NavItem = { to: string; label: string; end?: boolean };

type NavGroup = { label: string; items: NavItem[] };

/** Menú desplegable. Con `items` es una lista; con `groups` son submenús en
 *  columnas, cada uno con su encabezado (p. ej. Datos → Precios | Reportes). */
function NavDropdown({
  label,
  items,
  groups,
}: {
  label: string;
  items?: NavItem[];
  groups?: NavGroup[];
}) {
  const todos = items ?? (groups ?? []).flatMap((g) => g.items);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Cerrar al hacer click fuera.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Cerrar al navegar (cambio de pathname).
  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Activo si alguna ruta hija coincide con la URL actual.
  const hasActive = todos.some((i) =>
    i.end
      ? location.pathname === i.to
      : location.pathname === i.to || location.pathname.startsWith(i.to + "/"),
  );

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 px-2 py-1 rounded ${
          hasActive
            ? "bg-slate-100 text-slate-900"
            : "text-slate-600 hover:bg-slate-50"
        }`}
      >
        {label}
        <ChevronDown
          className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div
          className={`absolute top-full left-0 mt-1 bg-white border rounded-md shadow-lg z-50 py-1 ${
            groups ? "flex gap-2 px-1" : "min-w-[180px]"
          }`}
        >
          {(groups ?? [{ label: "", items: todos }]).map((g) => (
            <div key={g.label} className={groups ? "min-w-[220px]" : ""}>
              {g.label && (
                <div className="px-3 pt-1.5 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">
                  {g.label}
                </div>
              )}
              {g.items.map((i) => (
                <NavLink
                  key={i.to}
                  to={i.to}
                  end={i.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `block px-3 py-1.5 text-sm whitespace-nowrap ${
                      isActive
                        ? "bg-blue-50 text-blue-700 font-medium"
                        : "text-slate-700 hover:bg-slate-50"
                    }`
                  }
                >
                  {i.label}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
